import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { Database, GripVertical, Trash2, CheckCircle2, AlertCircle, Plus } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

interface EnrichmentSettings {
  providerOrder: string[];
  monthlyCreditCap: number;
  configuredProviders: string[];
}

interface ProviderFieldDef {
  key: string;
  label: string;
  type: string;
}

interface CredentialListResponse {
  configuredProviders: string[];
  allProviders: string[];
  providerFields: Record<string, ProviderFieldDef[]>;
}

// Providers that need zero credentials (free public registries).
const FREE_PROVIDERS = new Set(['RUES', 'CAMARAS_COMERCIO', 'STUB']);

const PROVIDER_LABELS: Record<string, string> = {
  APOLLO: 'Apollo.io',
  ZOOMINFO: 'ZoomInfo',
  LINKEDIN: 'LinkedIn (Proxycurl)',
  RUES: 'RUES Colombia (free)',
  CAMARAS_COMERCIO: 'Cámaras de Comercio (free)',
  STUB: 'Stub (dev/test)',
};

/**
 * Story 78.4 — /admin/enrichment settings page.
 *
 * Features:
 *  - Provider order drag-and-drop (first-hit-wins waterfall)
 *  - Monthly credit cap field
 *  - Per-provider credential configuration (encrypted via ApiKeyEncryption)
 *  - RUES / Cámaras de Comercio shown as "no credentials needed"
 */
export function EnrichmentSettingsPage() {
  const { t } = useTranslation();
  const [, setSettings] = useState<EnrichmentSettings | null>(null);
  const [credentials, setCredentials] = useState<CredentialListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [saving, setSaving] = useState(false);
  const [capDraft, setCapDraft] = useState('');
  const [orderDraft, setOrderDraft] = useState<string[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [credDraft, setCredDraft] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiClient.get<EnrichmentSettings>('/enrichment/settings'),
      apiClient.get<CredentialListResponse>('/enrichment/credentials'),
    ])
      .then(([settingsRes, credRes]) => {
        setSettings(settingsRes.data);
        setOrderDraft(settingsRes.data.providerOrder);
        setCapDraft(String(settingsRes.data.monthlyCreditCap));
        setCredentials(credRes.data);
      })
      .catch(() => setError(t('enrichment.error_load')))
      .finally(() => setLoading(false));
  }, [refreshToken, t]);

  function handleDragEnd(result: DropResult) {
    if (!result.destination) return;
    const next = [...orderDraft];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setOrderDraft(next);
  }

  function addProvider(provider: string) {
    if (!orderDraft.includes(provider)) {
      setOrderDraft([...orderDraft, provider]);
    }
  }

  function removeFromOrder(provider: string) {
    setOrderDraft(orderDraft.filter((p) => p !== provider));
  }

  async function handleSaveSettings(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await apiClient.put('/enrichment/settings', {
        providerOrder: orderDraft,
        monthlyCreditCap: parseInt(capDraft, 10) || 0,
      });
      setSaved(true);
      setRefreshToken((x) => x + 1);
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('enrichment.error_save_settings'));
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveCredentials(e: FormEvent, provider: string) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.put(`/enrichment/credentials/${provider}`, { fields: credDraft });
      setEditing(null);
      setCredDraft({});
      setRefreshToken((x) => x + 1);
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('enrichment.error_save_creds'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemoveCredentials(provider: string) {
    try {
      await apiClient.delete(`/enrichment/credentials/${provider}`);
      setRefreshToken((x) => x + 1);
    } catch {
      setError(t('enrichment.error_remove_creds'));
    }
  }

  const allProviders = credentials?.allProviders ?? [];
  const configuredSet = new Set(credentials?.configuredProviders ?? []);
  const providerFields = credentials?.providerFields ?? {};
  const unusedProviders = allProviders.filter((p) => !orderDraft.includes(p));

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center gap-3">
        <Database className="h-5 w-5 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold">{t('enrichment.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('enrichment.subtitle')}</p>
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      {saved && (
        <div className="rounded-md border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-300">
          {t('enrichment.saved')}
        </div>
      )}

      {/* Waterfall Order + Cap */}
      <form onSubmit={handleSaveSettings} className="space-y-4">
        <Card className="space-y-4">
          <h2 className="text-lg font-semibold">{t('enrichment.waterfall_order_title')}</h2>
          <p className="text-sm text-muted-foreground">{t('enrichment.waterfall_order_hint')}</p>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="provider-order">
              {(provided) => (
                <ul
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="space-y-2"
                >
                  {orderDraft.map((provider, index) => (
                    <Draggable key={provider} draggableId={provider} index={index}>
                      {(dragProvided, snapshot) => (
                        <li
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className={`flex items-center gap-3 rounded-lg border bg-background px-3 py-2 text-sm shadow-sm ${
                            snapshot.isDragging ? 'shadow-md' : ''
                          }`}
                        >
                          <span {...dragProvided.dragHandleProps}>
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                          </span>
                          <span className="flex-1 font-medium">
                            {PROVIDER_LABELS[provider] ?? provider}
                          </span>
                          <span className="text-xs text-muted-foreground">#{index + 1}</span>
                          {configuredSet.has(provider) || FREE_PROVIDERS.has(provider) ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <span title={t('enrichment.not_configured')}>
                              <AlertCircle className="h-4 w-4 text-amber-500" />
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => removeFromOrder(provider)}
                            className="ml-1 text-muted-foreground hover:text-destructive"
                            title={t('enrichment.remove_from_order')}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>

          {unusedProviders.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {unusedProviders.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => addProvider(p)}
                  className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
                >
                  <Plus className="h-3 w-3" />
                  {PROVIDER_LABELS[p] ?? p}
                </button>
              ))}
            </div>
          )}

          <div className="max-w-xs space-y-1">
            <label className="text-sm font-medium">{t('enrichment.monthly_cap_label')}</label>
            <p className="text-xs text-muted-foreground">{t('enrichment.monthly_cap_hint')}</p>
            <Input
              type="number"
              min={0}
              value={capDraft}
              onChange={(e) => setCapDraft(e.target.value)}
              placeholder="0"
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </Card>
      </form>

      {/* Per-provider credentials */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t('enrichment.credentials_title')}</h2>
        <p className="text-sm text-muted-foreground">{t('enrichment.credentials_hint')}</p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {allProviders.map((provider) => {
            const isFree = FREE_PROVIDERS.has(provider);
            const isConfigured = configuredSet.has(provider) || isFree;
            const fields = providerFields[provider] ?? [];
            const isEditing = editing === provider;

            return (
              <Card key={provider} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{PROVIDER_LABELS[provider] ?? provider}</h3>
                  {isConfigured ? (
                    <span className="rounded-full bg-emerald-900/40 px-2 py-0.5 text-xs font-medium text-emerald-200">
                      {t('enrichment.status_configured')}
                    </span>
                  ) : (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      {t('enrichment.status_unconfigured')}
                    </span>
                  )}
                </div>

                {isFree && (
                  <p className="text-xs text-muted-foreground">{t('enrichment.free_registry_note')}</p>
                )}

                {!isFree && isEditing && (
                  <form onSubmit={(e) => handleSaveCredentials(e, provider)} className="space-y-2">
                    {fields.map((field) => (
                      <div key={field.key} className="space-y-0.5">
                        <label className="text-xs font-medium">{field.label}</label>
                        <Input
                          type={field.type ?? 'text'}
                          value={credDraft[field.key] ?? ''}
                          onChange={(e) => setCredDraft({ ...credDraft, [field.key]: e.target.value })}
                          required
                        />
                      </div>
                    ))}
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditing(null);
                          setCredDraft({});
                        }}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={submitting || fields.some((f) => !(credDraft[f.key] ?? '').trim())}
                      >
                        {submitting ? t('common.saving') : t('common.save')}
                      </Button>
                    </div>
                  </form>
                )}

                {!isFree && !isEditing && (
                  <div className="flex justify-end gap-2">
                    {isConfigured && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemoveCredentials(provider)}
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        {t('enrichment.remove')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditing(provider);
                        setCredDraft({});
                        setError(null);
                      }}
                    >
                      {isConfigured ? t('enrichment.replace') : t('enrichment.configure')}
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      <p className="text-xs text-muted-foreground">{t('enrichment.security_note')}</p>
    </div>
  );
}
